import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { filter } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import cloneDeep from 'lodash-es/cloneDeep';
import { BasketService } from 'src/app/services/basket.service';
import { DataService } from 'src/app/services/data.service';
import { Food } from 'src/interfaces/food.model';
import { Units } from 'src/interfaces/enums.model';
import { Modificators } from 'src/interfaces/modificators.model';
import { v4 as uuidv4 } from 'uuid';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { LocalizationService } from 'src/app/services/localization.service';
import { ImageCacheService } from 'src/app/services/image-cache.service';
import { Locale } from 'src/interfaces/locale.model';
import { NO_PHOTO } from 'src/app/utils/no-photo';
import { SheetDirective } from 'src/app/directives/sheet.directive';

@Component({
    selector: 'app-menu-food-dialog',
    templateUrl: './menu-food-dialog.component.html',
    styleUrls: ['./menu-food-dialog.component.css'],
    animations: [
        trigger('slideToggle', [
            state('void', style({ height: '0px', opacity: 0 })),
            state('*', style({ height: '*', opacity: 1 })),
            transition('void <=> *', animate('200ms ease-in-out'))
        ])
    ],
    standalone: false
})
export class MenuFoodDialogComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    private dialogRef: MatDialogRef<MenuFoodDialogComponent>,
    private dataService: DataService,
    private basketService: BasketService,
    private localization: LocalizationService,
    private imageCache: ImageCacheService,
    @Inject(MAT_DIALOG_DATA) public data: Food // Принимаем объект Food
  ) {
    this.locale = this.dataService.locale;
    this.food = this.data;
    this.kkal = this.food.kkal;
    this.original = cloneDeep(this.food);
    this.isInfoVisible = false;

    // Затемнение и Esc закрывают окно с анимацией, а не мгновенно
    this.dialogRef.disableClose = true;
    this.dialogRef.backdropClick().subscribe(() => this.close());
    this.dialogRef.keydownEvents()
      .pipe(filter((event) => event.key === 'Escape'))
      .subscribe(() => this.close());
  }

  @ViewChild('scrollContent') scrollContent!: ElementRef;
  @ViewChild('titleOriginal') titleOriginal!: ElementRef;
  @ViewChild(SheetDirective) sheet!: SheetDirective;
  titleOffset: number = 12;

  food: Food;
  original: Food;
  isBasket: boolean = false;
  canUpdate: boolean = false;

  locale: Locale | null = null;

  // Калории
  kkal: number | null;

  // Изображение заглушка
  imageSrc: string = NO_PHOTO.food;

  /** Нет фото или не загрузилось — векторная заглушка, как в списке */
  get noPhoto(): boolean {
    return this.imageSrc === NO_PHOTO.food;
  }

  // Модификаторы
  modificators: Modificators | undefined = undefined;

  isInfoVisible: boolean;

  /** Количество и «Добавить» — только со столом (DataService.canOrder) */
  get canOrder(): boolean {
    return this.dataService.canOrder();
  }

  ngOnInit(): void {
    if (this.food.basketId) {
      this.isBasket = true;
      this.canUpdate = false;
    } else {
      this.food.count = this.food.count || 1;
      this.original.count = this.food.count || 1;
    }

    // Image
    if (this.food?.image?.trim()) {
      this.imageCache.getImage(this.food.image, NO_PHOTO.food).then(src => {
        this.imageSrc = src;
      });
    }

    if (this.food.variability && this.food.variants.length > this.food.choise) {
      // Цена блюда
      this.food.price = (this.food.variants[this.food.choise].price ?? this.food.price);
      // Объем блюда
      this.food.size = (this.food.variants[this.food.choise].size ?? this.food.size);
    }

    // Расчитываем калории на всё блюдо, если энергетическая ценность на 100 г
    if (this.food.energy && this.food.kkal && (this.food.unit === Units.weight || this.food.unit === Units.volume) && this.food.size) {
      this.kkal = this.food.size / 100 * this.food.kkal;
    }

    // Загружаем модификаторы
    const modificators = typeof this.food.modificators === 'object' ? this.food.modificators : undefined;
    if (modificators) {
      this.modificators = modificators;
    }

    this.updatePrice();
  }

  ngAfterViewInit() {
    this.scrollContent.nativeElement.addEventListener('scroll', this.onScroll.bind(this));
    this.titleOffset = this.titleOriginal.nativeElement.offsetTop - 4;
  }

  onScroll(event: any) {
    const scrollOffset = this.scrollContent.nativeElement.scrollTop;
    
    const title = document.querySelector('.food-dialog-title__header')!
    // console.log('MenuFoodDialog | onScroll() | scrollOffset:', scrollOffset, ' | titleOffset:', this.titleOffset);
    if (scrollOffset > this.titleOffset) {
      title.classList.add('show');
    } else {
      title.classList.remove('show');
    }
  }

  onModificatorsChange() {
    console.log('MenuFoodDialog | onModificatorsChange()');
    this.updatePrice();
    this.checkCanUpdateBasket();
  }

  onVariantsChange() {
    console.log('MenuFoodDialog | onVariantsChange() | choise:', this.food.choise);
    this.updatePrice();
    
    // Объем
    this.food.size = (this.food.variants[this.food.choise].size ?? this.food.size);

    // Расчитываем калории на всё блюда, если энергетическая ценность на 100 г/мл
    if (this.food.energy && this.food.kkal && (this.food.unit === Units.weight || this.food.unit === Units.volume) && this.food.size) {
      this.kkal = this.food.size / 100 * this.food.kkal;
    }

    // Проверка корзины, можно обновить или добавить
    this.checkCanUpdateBasket();
  }

  increment(): void {
    this.food.count++;
    this.checkCanUpdateBasket();
  }

  decrement(): void {
    if (this.food.count > 1) {
      this.food.count--;
      this.checkCanUpdateBasket();
    }
  }

  buttonPushRemoveFoodBasket(): void {
    if (!this.isBasket) {

      // Добавляем блюдо в корзину
      console.log('MenuFoodDialog | buttonPushRemoveFoodBasket() | pushFoodToBasket');
      this.food.basketId = uuidv4();
      this.basketService.pushFoodToBasket(this.food);
    } else {
      if (this.canUpdate) {

        // Обновляем блюдо в корзине
        console.log('MenuFoodDialog | buttonPushRemoveFoodBasket() | pushFoodToBasket');
        this.basketService.updateFoodInBasket(this.food);
      } else {

        // Удаляем блюдо из корзины
        console.log('MenuFoodDialog | buttonPushRemoveFoodBasket() | removeFoodDromBasket');
        this.basketService.removeFoodFromBasket(this.food);
      }
    } 
    this.close();
  }

  /** Окно уезжает вниз, затем диалог закрывается (onSheetClosed) */
  close(): void {
    this.sheet.dismiss();
  }

  onSheetClosed(): void {
    this.dialogRef.close();
  }

  updatePrice(): void {
    let total = 0;
    // Цена за вариант (если есть)
    if (this.food.variability && this.food.variants.length) {
      this.food.price = this.food.variants[this.food.choise].price ?? this.food.price;
    } 

    // Начальная цена за единицу
    total = this.food.price;

    const modificators = typeof this.food.modificators === 'object' ? this.food.modificators : null;
    console.log('MenuFoodDialog | updatePrice() | modificators:', modificators?.items.map(item => `${item.title}: ${item.count} шт.`));
    modificators?.items.forEach(item => {
      if (item.count != null && item.count > 0) {
        total += item.price * item.count
      }
    });

    this.food.totalPrice = total;
    console.log('Food | updatePrice() | count:', this.food.count, ' | price:', this.food.totalPrice);
  }

  private checkCanUpdateBasket(): void {
    if (this.isBasket) {

      // Изменился вариант блюда
      if (this.food.choise != this.original.choise) {
        this.canUpdate = true;
        console.log('MenuFoodDialog | checkCanUpdateBasket() | canUpdate:', this.canUpdate);
        return
      }

      // Изменилось количество
      if (this.food.count != this.original.count) {
        this.canUpdate = true;
        console.log('MenuFoodDialog | checkCanUpdateBasket() | canUpdate:', this.canUpdate);
        return
      }

      // Проверяем, есть ли изменения в модификаторах
      const current = typeof this.food.modificators === 'object' ? this.food.modificators : undefined;

      // Модификаторы есть
      if (current && current.items.length > 0) {
        // console.log('MenuFoodDialog | checkCanUpdateBasket() | Modificator groups:', groups.length);
        
        const modificators = typeof this.original.modificators === 'object' ? this.original.modificators : undefined;
        if (modificators && modificators.items.length > 0) {
          // console.log('MenuFoodDialog | checkCanUpdateBasket() | modificators:', modificators.items.length, modificatorsCurrent.items.length,);
          
          // Проход по элементам
          for (let i = 0; i < modificators.items.length; i++) {
            
            // Проверяем количество
            if (typeof modificators.items[i] != 'object' || typeof current.items[i] != 'object') {
              this.canUpdate = false;
              return
            }
            if (modificators.items[i].count != current.items[i].count) {
              this.canUpdate = true;
              console.log('MenuFoodDialog | checkCanUpdateBasket() | canUpdate:', this.canUpdate);
              return
            }
          }
        }
      }
      
      this.canUpdate = false;
      console.log('MenuFoodDialog | checkCanUpdateBasket() | canUpdate:', this.canUpdate);

    } else {
      this.canUpdate = false;
    }
  }

  toggleInfo() {
    this.isInfoVisible = !this.isInfoVisible;
  }

  get roundedKkal(): string {
    return this.kkal ? this.kkal.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '';
  }

  get localSize(): string {
    return this.food.size ? this.food.size.toLocaleString('ru-RU', { useGrouping: true }) : '';
  }

  // Отписка от событий при уничтожении компонента
  ngOnDestroy() {
    this.scrollContent.nativeElement.removeEventListener('scroll', this.onScroll.bind(this));
  }
}

